import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CompanyModuleForCarrierOutDTO,
  ExternalIdTypeEnum,
  GetDetailLinkedCompanyResponseOutDTO,
  ModulesEnum,
  ModuleTypeEnum,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import SvgComponent from '@icons/component-inverted.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import InputTextTag from '@components/RingilComponents/tags/InputTextTag.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

interface ComponentProps {
  partnerData: GetDetailLinkedCompanyResponseOutDTO;
  reloadData: () => void;
  // Reload after enabling/disabling a module. Handles the case where removing the last active
  // module revokes the current user's access to this partner (403 -> back to partners list).
  reloadAfterModuleChange: () => void;
}

const PartnerDetailAbout: React.FC<ComponentProps> = ({ partnerData, reloadData, reloadAfterModuleChange }) => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const {
    getActiveLinkedCodes,
    lovExternalIdTypes,
    lovEnabledCompanyModule,
    setModuleToCompany,
    setExternalIds,
  } = useContext(ApiContext);

  // Typovat jako klíče enumu aby TS věděl, že lze enum indexovat
  const [externalIdTypes, setExternalIdTypes] = useState<Array<keyof typeof ExternalIdTypeEnum>>([]);
  const [enabledModules, setEnabledModules] = useState<ModulesEnum[]>([]);

  const editModulesEnabled = hasPermission([PermissionTypesEnum.PERMISSION_SETTINGS_PARTNERS_RW]);

  const modulesTableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  useEffect(() => {
    getActiveLinkedCodes(data => {
      const modulesArray = data.flatMap(item => item.modules ?? []);
      const modulesSet = new Set(modulesArray);
      setEnabledModules(modulesSet.size > 0 ? Array.from(modulesSet) : []);
    });
    // Přetypování příchozích dat na pole klíčů enumu
    lovExternalIdTypes(data => setExternalIdTypes(data as Array<keyof typeof ExternalIdTypeEnum>));
  }, []);

  useEffect(() => {
    modulesTableRef.current?.reloadData();
  }, [partnerData]);

  const loadDataApiModules = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CompanyModuleForCarrierOutDTO>) => void) => {
    lovEnabledCompanyModule(
      data => {
        const modules = partnerData.companyModules.filter(module => module.enabled).map(module => module.moduleType);

        const items = (data as Array<string | ModulesEnum>)
          .filter(moduleItem => enabledModules.includes(moduleItem as ModulesEnum))
          .map(item => {
            const key = item as keyof typeof ModuleTypeEnum;
            const moduleType = ModuleTypeEnum[key] as ModuleTypeEnum;
            return {
              moduleType,
              enabled: modules.includes(moduleType),
            };
          });

        const pagedData: PagedData<CompanyModuleForCarrierOutDTO> = {
          data: items,
          total: items.length,
          page: 1,
          size: items.length,
        };
        onSuccess(pagedData);
      },
    );
  };

  const modulesColumnsConfig: ColumnConfig<CompanyModuleForCarrierOutDTO>[] = [
    {
      id: 'moduleType',
      header: 'CarrierDetailModules.columnHeaderModuleName',
      headerDefault: 'Name',
      width: '40%',
      order: 1,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.moduleType}
          </div>
        );
      },
    },
    {
      id: 'enabled',
      header: 'CarrierDetailModules.columnHeaderEnabled',
      headerDefault: 'Enabled',
      width: '40%',
      order: 2,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.enabled ? tr('CarrierDetailModules.enabled', 'Enabled') : tr('CarrierDetailModules.disabled', 'Disabled')}
          </div>
        );
      },
    },
    {
      id: 'actions',
      width: '20%',
      sortable: false,
      order: 3,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {editModulesEnabled && (
              <RingilButton
                label={data.enabled ? tr('CarrierDetailModules.disable', 'Disable') : tr('CarrierDetailModules.enable', 'Enable')}
                mode={'table'}
                onClick={() => {
                  setModuleToCompany(partnerData.companyInfo.companyId, data.moduleType, { enabled: !data.enabled }, () => {
                    reloadAfterModuleChange();
                  });
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Ringil3BadgeRow>
        {externalIdTypes
          .sort((a, b) => String(a).localeCompare(String(b)))
          .map((externalIdTypeItem) => {
            const externalId = partnerData.externalIdsInfo.find(id => id.externalIdType === externalIdTypeItem);
            return (
              <InputTextTag
                key={externalIdTypeItem}
                label={tr(`PartnerDetailAbout.partnerIdLabel${externalIdTypeItem}`, `${externalIdTypeItem} id`)}
                value={externalId?.externalIds}
                onChange={v => {
                  // ExternalIdTypeEnum lze nyní indexovat bezpečně, protože externalIdTypeItem je keyof typeof ExternalIdTypeEnum
                  const externalIdType = ExternalIdTypeEnum[externalIdTypeItem];
                  setExternalIds(partnerData.companyInfo.companyId, {
                      externalIds: v,
                      externalIdType,
                    },
                    () => {
                      reloadData();
                    },
                  );
                }}
              />
            );
          })
        }
      </Ringil3BadgeRow>

      <div className={'flex flex-row'}>
        <COHeadline
          variant={'h2'}
          title={tr('CarrierDetailAbout.headlineCarrierModules', 'Carrier modules')}
          icon={<SvgComponent className={'w-4 h-4 text-content-tertiary'} />}
        />
      </div>
      <RingilDataTable
        ref={modulesTableRef}
        columnConfig={modulesColumnsConfig as ColumnConfig<unknown>[]}
        loadDataFromApi={loadDataApiModules}
        filterName={'partner_detail_modules_list'}
      />
    </>
  );
};


export default PartnerDetailAbout;